/**
 * test-dual — experimental two-ground-lane battlefield (M66).
 *
 * Not a campaign map: it exists to exercise the M65 multi-route runtime and to
 * measure how two simultaneous ground lanes change tower placement value.
 *
 * Layout intent
 * - GROUND A owns the west half and is the longer lane (more corners).
 * - GROUND B owns the east half and is noticeably shorter (leaks sooner).
 * - Both lanes bend into a narrow middle band around x=11..12, so a single
 *   short-range tower there covers both; anywhere else it covers one lane.
 * - AIR A crosses the same middle band, so the premium centre is contested by
 *   three lanes at once.
 */

import type { MapDefinition, WaveDefinition } from '../model/types'
import {
  airRoute,
  emptyGrid,
  groundRoute,
  horiz,
  joinPaths,
  rowsFromGrid,
  setPath,
  vert
} from './gridPaint'

export const TEST_DUAL_WIDTH = 20
export const TEST_DUAL_HEIGHT = 26

/** West lane: top-left spawn, two hairpins, centre band, bottom-left exit. */
function laneA() {
  return joinPaths([
    vert(2, 0, 3),
    horiz(3, 2, 6),
    vert(6, 3, 6),
    horiz(6, 6, 2),
    vert(2, 6, 9),
    horiz(9, 2, 7),
    vert(7, 9, 13),
    horiz(13, 7, 10),
    vert(10, 13, 16),
    horiz(16, 10, 5),
    vert(5, 16, 21),
    horiz(21, 5, 2),
    vert(2, 21, 25)
  ])
}

/** East lane: top-right spawn, shallower zigzag, centre band, bottom exit. */
function laneB() {
  return joinPaths([
    vert(17, 0, 4),
    horiz(4, 17, 13),
    vert(13, 4, 9),
    horiz(9, 13, 17),
    vert(17, 9, 12),
    horiz(12, 17, 13),
    vert(13, 12, 16),
    horiz(16, 13, 17),
    vert(17, 16, 20),
    horiz(20, 17, 14),
    vert(14, 20, 25)
  ])
}

/** Air lane: bottom-left to top-right, threading the contested middle band. */
function laneAir() {
  return [
    { x: 0, y: 25 },
    { x: 0, y: 21 },
    { x: 7, y: 21 },
    { x: 7, y: 17 },
    { x: 12, y: 17 },
    { x: 12, y: 9 },
    { x: 19, y: 9 },
    { x: 19, y: 0 }
  ]
}

/**
 * Deterministic lane-pressure waves. Every group names its routeId so a harness
 * can assert distribution without depending on default-lane resolution.
 */
export function testDualWaves(): WaveDefinition[] {
  return [
    {
      // Baseline: only the long west lane is live.
      id: 'dual-w1-normal-a',
      completionReward: 40,
      groups: [
        { enemy: 'normal', count: 8, interval: 1.1, delay: 0.5, routeId: 'ground-a' }
      ]
    },
    {
      // Baseline: only the short east lane is live — same map, other side.
      id: 'dual-w2-fast-b',
      completionReward: 45,
      groups: [{ enemy: 'fast', count: 8, interval: 0.7, delay: 0.5, routeId: 'ground-b' }]
    },
    {
      // Same definition on both lanes at the same time (split attention).
      id: 'dual-w3-both-normal',
      completionReward: 55,
      groups: [
        { enemy: 'normal', count: 8, interval: 1.0, delay: 0.5, routeId: 'ground-a' },
        { enemy: 'normal', count: 8, interval: 1.0, delay: 0.5, routeId: 'ground-b' }
      ]
    },
    {
      // Asymmetric threat: slow armour west, sprinters east.
      id: 'dual-w4-tank-a-fast-b',
      completionReward: 65,
      groups: [
        { enemy: 'tank', count: 4, interval: 1.8, delay: 0.5, routeId: 'ground-a' },
        { enemy: 'fast', count: 10, interval: 0.55, delay: 0.5, routeId: 'ground-b' }
      ]
    },
    {
      // Air only: ground towers idle unless they also cover the air band.
      id: 'dual-w5-air',
      completionReward: 70,
      groups: [{ enemy: 'flyer', count: 8, interval: 0.9, delay: 0.5, routeId: 'air-a' }]
    }
  ]
}

export function buildTestDualMap(): MapDefinition {
  const width = TEST_DUAL_WIDTH
  const height = TEST_DUAL_HEIGHT
  const grid = emptyGrid(width, height)

  const a = laneA()
  const b = laneB()
  setPath(grid, a)
  setPath(grid, b)

  return {
    schemaVersion: 1,
    id: 'test-dual',
    name: 'Test Dual Lane',
    width,
    height,
    rows: rowsFromGrid(grid),
    // Lane A stays mirrored in the legacy fields for single-route readers.
    spawn: { ...a[0]! },
    exit: { ...a[a.length - 1]! },
    path: a.map((c) => ({ ...c })),
    startingGold: 600,
    startingLives: 20,
    waves: testDualWaves(),
    routes: [groundRoute('ground-a', a), groundRoute('ground-b', b), airRoute('air-a', laneAir())]
  }
}

export const TEST_DUAL_MAP: MapDefinition = buildTestDualMap()
